import {
  BaseDataGrid,
  ButtonRow,
  DATE_FORMAT_ISO_NO_TIME,
  Dayjs,
  getFilteredColDefs,
  useGridServerPagination,
  useModal,
  useSearchParamsDate,
  useSearchParamsForm,
} from "@synota-io/synota-shared-ui";
import { generatePath, Link, useLocation, useParams } from "react-router-dom";
import { adhocFields } from "../fields/adhocFields";
import { IAdhocInvoice, useAdhocInvoices } from "../../../../api/hooks/useAdhocInvoices";
import {
  DataGridProps,
  GridColumnVisibilityModel,
  GridInputRowSelectionModel,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { TableSummaryToolbar } from "../../../../shared/datagrid/TableSummaryToolbar";
import { TableDateFiltersToolbar } from "../../../../shared/datagrid/TableDateFiltersToolbar";
import { Button } from "@mui/material";
import { CONTRACT_ADHOC_INVOICES_PATH } from "../../../../paths";
import { useContractDetails } from "../../../../api/hooks/useContractDetails";

type Props = Pick<
  DataGridProps<IAdhocInvoice>,
  "slots" | "slotProps" | "onRowDoubleClick" | "sx"
> & {
  summary?: boolean;
};

export const AdhocInvoicesTable = ({ summary, ...props }: Props) => {
  const [rowSelectionModel, onRowSelectionModelChange] = useState<GridInputRowSelectionModel>([]);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});

  const { state } = useLocation();
  const { control, watch } = useSearchParamsForm<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  }>(
    {
      defaultValues: {
        end_date: null,
        start_date: null,
      },
    },
    {
      start_date: useSearchParamsDate(),
      end_date: useSearchParamsDate(),
    },
  );

  const modal = useModal();
  const values = watch();

  const params = useParams<{
    id: string;
    uuid?: string;
  }>();

  const [paginationModel, onPaginationModelChange] = useState<GridPaginationModel>(() =>
    summary
      ? { page: 0, pageSize: 5 }
      : {
          page: 0,
          pageSize: 30,
        },
  );

  const input = {
    contract_uuid: params.id || "",
    energy_date_start: values.start_date?.format(DATE_FORMAT_ISO_NO_TIME),
    energy_date_end: values.end_date?.format(DATE_FORMAT_ISO_NO_TIME),
  };

  const {
    invoices,
    hasNextPage,
    isLoading: isLoadingInvoices,
    isFetching,
  } = useAdhocInvoices({
    ...input,
    page: paginationModel.page,
    number_to_return: paginationModel.pageSize,
  });

  const { isPending: isPendingContract } = useContractDetails({
    id: params.id,
  });

  const paginationProps = useGridServerPagination({
    hasNextPage,
    currentPageRowCount: invoices.length,
    paginationModel,
    onPaginationModelChange,
  });

  const columns = useMemo(() => {
    const defaultColumns = getFilteredColDefs(
      adhocFields,
      summary ? "summary" : "table",
      summary,
    ).map((column) => ({
      ...column,
      flex: 1,
    }));

    return defaultColumns;
  }, [summary]);

  useEffect(() => {
    if (!modal.open && params.uuid && !isFetching) {
      modal.onOpen();
    }
  }, [isFetching, modal, params.uuid]);

  useEffect(() => {
    if (state && "selectedRow" in state && state.selectedRow) {
      onRowSelectionModelChange([state.selectedRow]);
    }
  }, [state]);

  const isLoading = isLoadingInvoices || isPendingContract;

  return (
    <>
      <BaseDataGrid
        {...props}
        sx={summary ? { ...props.sx, minHeight: "280px" } : props.sx}
        slots={{
          footer: summary
            ? () => (
                <ButtonRow borderTop="solid" borderColor="background.default">
                  <Button
                    component={Link}
                    to={generatePath(CONTRACT_ADHOC_INVOICES_PATH, { id: params.id! })}
                    size="small"
                    fullWidth
                  >
                    See all
                  </Button>
                </ButtonRow>
              )
            : undefined,
          toolbar: summary
            ? TableSummaryToolbar
            : (props) => (
                <TableDateFiltersToolbar control={control} isLoading={isLoading} {...props} />
              ),
          ...props.slots,
        }}
        density={summary ? "compact" : "standard"}
        getRowId={(row) => row.shared_uuid}
        columns={columns}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={setColumnVisibility}
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        autosizeOnMount={!summary}
        {...paginationProps}
        loading={isLoading}
        rows={invoices || []}
      />
    </>
  );
};
